import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { AuthContext } from "../App";
import { useCart } from "../context/CartContext";
import CartDropdown from "../components/CartDropdown";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { 
  Flower2, 
  ShoppingBag, 
  Check, 
  ChevronDown,
  LogOut,
  Sparkles,
  Palette,
  FileText,
  Gift,
  Globe,
  ArrowRight,
  Download,
  Package,
  ExternalLink,
  Rocket,
  GraduationCap,
  TrendingUp,
  ShoppingCart,
  Trash2,
  FlaskConical,
  User,
  AlertCircle,
  Lock,
  HelpCircle,
  ChevronRight
} from "lucide-react";
import { MODES } from "../config/ModeLock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";

/**
 * ============================================================================
 * MODE 2 → MODE 3 TRANSITION PAGE
 * ============================================================================
 * This page operates in two modes:
 * 
 * MODE 2 — FOUNDATION BROWSING (Initial View)
 *   Purpose: Explore complete brand foundations
 *   - Foundation names visible
 *   - What's included visible
 *   - NO automatic pricing display
 *   - NO default checkout prompts
 *   - Pricing revealed ONLY after selection
 * 
 * MODE 3 — OWNERSHIP & PURCHASE (After Selection)
 *   Purpose: Intentional acquisition
 *   - Pricing visible
 *   - Domain inclusion visible
 *   - Ownership terms visible
 *   - Checkout actions available
 *   - ENTRY: User must explicitly select a foundation
 * 
 * DEVELOPER NOTE:
 * Directional pages are non-commercial by design.
 * Conversion is intentionally delayed.
 * ============================================================================
 */

// ============================================================
// CHECKOUT GATE RULES (HARD RULE — DO NOT REMOVE)
// ============================================================
// Brand browsing and cart access are public.
// Checkout is restricted to signed-in users only.
// No pricing, ownership, or checkout actions occur without authentication.
// ============================================================

// ============================================================
// ISOLATED COMMERCE FLOW: Ready-Made Brand Store
// This page uses the global Ready-Made cart (CartContext)
// Completely isolated from Create-a-Brand activation flow
// ============================================================

const Store = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useContext(AuthContext);
  const { 
    addToReadyMadeCart, 
    removeFromReadyMadeCart, 
    clearReadyMadeCart,
    getReadyMadeCartCount,
    setLoginReturnUrl,
    activeCartContext,
    hasOppositeContextCart,
    CART_CONTEXTS
  } = useCart();
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState(null); // 'beginner' or 'advanced'
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [cartItem, setCartItem] = useState(null); // Item added to cart (local state for checkout flow)
  const [showContextWarning, setShowContextWarning] = useState(false); // Warning for context switch
  const [pricingTier, setPricingTier] = useState('brand_kit'); // 'brand_kit' ($197) or 'exclusive' ($297)
  const [expandedCards, setExpandedCards] = useState({}); // Track which cards have details expanded

  // Toggle expanded details for a brand card
  const toggleCardDetails = (brandId, e) => {
    e.stopPropagation(); // Don't select the card when clicking the link
    setExpandedCards(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  const experienceLevels = [
    {
      id: 'beginner',
      title: "I'm Just Starting Out",
      subtitle: "New to entrepreneurship",
      description: "Get step-by-step guidance, simplified tips, and foundational resources to launch your first product.",
      features: ["Beginner-friendly tutorials", "Simple launch checklist", "Basic marketing templates", "Startup cost guide"]
    },
    {
      id: 'advanced',
      title: "I'm an Experienced Entrepreneur",
      subtitle: "Ready to scale",
      description: "Access advanced strategies, scaling techniques, and professional resources to grow your business.",
      features: ["Advanced scaling strategies", "Wholesale & B2B guide", "Multi-channel marketing", "Financial projections template"]
    }
  ];

  // Check authentication on mount and when URL changes (for users who return after login)
  useEffect(() => {
    fetchStoreBrands();
  }, []);

  // Auto-scroll to checkout when returning from login with checkout param
  useEffect(() => {
    const checkoutParam = searchParams.get('checkout');
    if (checkoutParam === 'ready-made' && user) {
      // User returned from login - scroll to checkout section
      setTimeout(() => {
        document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [searchParams, user]);

  // Restore cart from sessionStorage after login OR auto-select from URL param
  useEffect(() => {
    if (brands.length > 0) {
      // First check URL param for direct selection from landing page
      const selectedFromUrl = searchParams.get('selected');
      if (selectedFromUrl) {
        const brandToSelect = brands.find(b => b.id === selectedFromUrl);
        if (brandToSelect) {
          setSelectedBrand(brandToSelect);
          setSelectedAddOns([]);
          // Scroll to checkout section
          setTimeout(() => {
            document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
          return; // Don't check sessionStorage if URL param was used
        }
      }
      
      // Then check sessionStorage for cart restoration after login
      const pendingCheckout = sessionStorage.getItem('pendingCheckout');
      if (pendingCheckout) {
        try {
          const { package_id, add_ons, experience_level, pricing_tier } = JSON.parse(pendingCheckout);
          const brandToSelect = brands.find(b => b.id === package_id);
          if (brandToSelect) {
            // Restore pricing tier first
            const restoredTier = pricing_tier || 'brand_kit';
            setPricingTier(restoredTier);
            setSelectedBrand(brandToSelect);
            setSelectedAddOns(add_ons || []);
            if (experience_level) {
              setExperienceLevel(experience_level);
            }
            // Calculate total based on restored pricing tier
            const basePrice = restoredTier === 'exclusive' ? 297 : 197;
            let total = basePrice;
            if (restoredTier === 'brand_kit') {
              if (add_ons?.includes("formula_bundle_3")) total += 47;
              if (add_ons?.includes("formula_bundle_5")) total += 77;
              if (add_ons?.includes("domain_setup")) total += 29;
            }
            // Auto-add to cart if user came from checkout flow
            const item = {
              brand: brandToSelect,
              experienceLevel: restoredTier === 'exclusive' ? 'ready-made' : experience_level,
              addOns: add_ons || [],
              pricingTier: restoredTier,
              total: total,
              cartContext: CART_CONTEXTS.READY_MADE
            };
            setCartItem(item);
            addToReadyMadeCart(item);
            toast.success("Your cart has been restored!");
            // Scroll to checkout
            setTimeout(() => {
              document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 500);
          }
          sessionStorage.removeItem('pendingCheckout');
        } catch (e) {
          console.error("Error restoring cart:", e);
        }
      }
    }
  }, [brands, searchParams]);

  const fetchStoreBrands = async () => {
    try {
      const response = await axios.get(`${API}/store/brands`);
      setBrands(response.data.brands);
    } catch (error) {
      console.error("Error fetching store brands:", error);
      toast.error("Failed to load store");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
      navigate("/", { replace: true });
    } catch (error) {
      navigate("/", { replace: true });
    }
  };

  const toggleAddOn = (addOnId) => {
    if (selectedAddOns.includes(addOnId)) {
      setSelectedAddOns(selectedAddOns.filter(id => id !== addOnId));
    } else {
      setSelectedAddOns([...selectedAddOns, addOnId]);
    }
  };

  const calculateTotal = () => {
    if (!selectedBrand) return 0;
    // Base price depends on tier
    let total = pricingTier === 'exclusive' ? 297.00 : 197.00;
    if (selectedAddOns.includes("formula_bundle_3")) total += 47.00;
    if (selectedAddOns.includes("formula_bundle_5")) total += 77.00;
    // Domain Setup add-on only for Brand Kit tier
    if (pricingTier === 'brand_kit' && selectedAddOns.includes("domain_setup")) total += 29.00;
    return total;
  };

  const addToCart = () => {
    if (!selectedBrand) {
      toast.error("Please select a brand package");
      return;
    }
    
    // For $197, default to 'beginner' if no experience level selected
    // For $297, always use 'ready-made'
    let finalExperienceLevel;
    if (pricingTier === 'exclusive') {
      finalExperienceLevel = 'ready-made';
    } else {
      finalExperienceLevel = experienceLevel || 'beginner'; // Default to beginner
    }

    // Check if user has items in Create Brand cart
    if (hasOppositeContextCart(CART_CONTEXTS.READY_MADE)) {
      setShowContextWarning(true);
      return;
    }
    
    const item = {
      brand: selectedBrand,
      experienceLevel: finalExperienceLevel,
      addOns: selectedAddOns,
      pricingTier: pricingTier,
      total: calculateTotal(),
      cartContext: CART_CONTEXTS.READY_MADE // Explicit context tagging
    };
    
    const result = addToReadyMadeCart(item);
    if (!result.success) {
      toast.error(result.message);
      return;
    }
    
    setCartItem(item);
    // ADD-TO-CART CONFIRMATION
    toast.success(
      <div>
        <p className="font-semibold">Added to cart</p>
        <p className="text-sm opacity-80">Your selection has been saved. Sign in when you&apos;re ready to complete checkout.</p>
      </div>,
      { duration: 4000 }
    );
    
    // Scroll to checkout section
    setTimeout(() => {
      document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  const removeFromCart = () => {
    if (cartItem?.brand?.id) {
      removeFromReadyMadeCart(cartItem.brand.id);
    }
    setCartItem(null);
    toast.info("Item removed from cart");
  };

  const handleLogin = () => {
    // Store selected items in sessionStorage before redirecting to login
    // This ensures user returns to Ready-Made Brand checkout (NOT Create-a-Brand)
    if (selectedBrand) {
      sessionStorage.setItem('pendingCheckout', JSON.stringify({
        package_id: selectedBrand.id,
        add_ons: selectedAddOns,
        experience_level: experienceLevel,
        pricing_tier: pricingTier
      }));
    }
    // Explicitly set the return URL to Store for Ready-Made checkout
    sessionStorage.setItem('checkoutReturnUrl', '/store?checkout=ready-made');
    // Always redirect back to Store page after login
    const redirectUrl = window.location.origin + '/store?checkout=ready-made';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCheckout = async () => {
    if (!selectedBrand) {
      toast.error("Please select a brand package");
      return;
    }

    // If not logged in, redirect to login first
    if (!user) {
      handleLogin();
      return;
    }

    setIsCheckingOut(true);
    try {
      const response = await axios.post(`${API}/store/checkout`, {
        package_id: selectedBrand.id,
        add_ons: selectedAddOns
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setIsCheckingOut(false);
    }
  };

  const addOns = [
    {
      id: "formula_bundle_3",
      name: "Formula Bundle (3 Formulas)",
      price: 47.00,
      description: "Get 3 additional product formulas for your niche"
    },
    {
      id: "formula_bundle_5",
      name: "Formula Bundle (5 Formulas)",
      price: 77.00,
      description: "Get 5 additional product formulas with advanced recipes"
    }
    // Note: Domain is included in Ready Made Brand price ($197) - not an add-on
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bloomwright Studio
              </span>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              {user && (
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/dashboard")}
                  className="text-[#78716C] hover:text-[#2C332E] hidden md:flex"
                >
                  Dashboard
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Cart Dropdown */}
            <CartDropdown />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#F5F0EB]" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-[#8DA399]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#8DA399]" />
                    </div>
                    <span className="text-[#2C332E] hidden sm:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-[#78716C]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={handleLogin}
                className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6"
                data-testid="store-login-btn"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>


      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Section 1: Brand Foundations Intro */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-semibold text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Brand Foundations — Designed to Save You Time
          </h1>
          <p className="text-[#78716C] max-w-2xl mx-auto mb-3">
            These curated brand foundation kits provide clear direction — visuals, tone, and business guidance — so you can move forward without second-guessing early decisions.
          </p>
          <p className="text-[#78716C] max-w-2xl mx-auto mb-4">
            Each foundation is designed to support clarity and momentum, not ownership or exclusivity.
          </p>
          <p className="text-xs text-[#A8A29E] max-w-xl mx-auto">
            Brand names shown are examples only. Domains are not included and are not sold as exclusive brands in this tier.
          </p>
        </div>

        {/* Path Selector */}
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12">
          {/* Brand Foundation Kit Option */}
          <button
            onClick={() => {
              setPricingTier('brand_kit');
              setSelectedBrand(null);
              setSelectedAddOns([]);
              setCartItem(null); // Clear cart when switching tiers
              setExperienceLevel(null); // Reset experience level
            }}
            className={`text-left p-6 rounded-xl border-2 transition-all duration-200 ${
              pricingTier === 'brand_kit'
                ? 'border-[#8DA399] bg-[#8DA399]/5'
                : 'border-[#E6E1DB] hover:border-[#8DA399]/50 bg-white'
            }`}
            data-testid="tier-brand-kit"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-[#2C332E]">Brand Foundation</h3>
            </div>
            <p className="text-2xl font-bold text-[#C77D63] mb-2">$197</p>
            <p className="text-sm text-[#78716C]">
              Direction and structure — domain chosen separately.
            </p>
            {pricingTier === 'brand_kit' && (
              <div className="mt-3 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#8DA399] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-[#8DA399] font-medium">Selected</span>
              </div>
            )}
          </button>

          {/* Exclusive Option */}
          <button
            onClick={() => {
              setPricingTier('exclusive');
              setSelectedBrand(null);
              setSelectedAddOns([]);
              setCartItem(null); // Clear cart when switching tiers
              setExperienceLevel(null); // Reset experience level
            }}
            className={`text-left p-6 rounded-xl border-2 transition-all duration-200 ${
              pricingTier === 'exclusive'
                ? 'border-[#C77D63] bg-[#C77D63]/5'
                : 'border-[#E6E1DB] hover:border-[#C77D63]/50 bg-white'
            }`}
            data-testid="tier-exclusive"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-[#2C332E]">Ready-Made Brand</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#C77D63]/10 text-[#C77D63]">Ownership</span>
            </div>
            <p className="text-2xl font-bold text-[#C77D63] mb-2">$297</p>
            <p className="text-sm text-[#78716C]">
              Domain included. Sold once. Removed after purchase.
            </p>
            {pricingTier === 'exclusive' && (
              <div className="mt-3 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#C77D63] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-[#C77D63] font-medium">Selected</span>
              </div>
            )}
          </button>
        </div>

        {/* Section Headers */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-[#2C332E]">
            {pricingTier === 'brand_kit' ? 'Brand Foundations' : 'Ownership-Ready Brands'}
          </h2>
          <p className="text-sm text-[#78716C]">
            {pricingTier === 'brand_kit' 
              ? 'Select a category to explore brand direction and structure.'
              : 'Each brand below is complete, exclusive, and ready for transfer.'
            }
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {/* Brand Cards - Filter based on pricing tier:
                $197 (brand_kit): Show ALL brands
                $297 (exclusive): Show ONLY brands with included_domain
            */}
            {brands
              .filter(brand => pricingTier === 'brand_kit' || brand.included_domain)
              .map((brand) => (
              <Card 
                key={brand.id}
                className={`bg-white rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col ${
                  selectedBrand?.id === brand.id 
                    ? 'border-[#C77D63] shadow-hover' 
                    : 'border-[#E6E1DB]/50 shadow-soft hover:shadow-hover'
                }`}
                onClick={() => {
                  setSelectedBrand(brand);
                  setTimeout(() => {
                    const experienceSection = document.querySelector('[data-testid="experience-section"]');
                    if (experienceSection) {
                      experienceSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                data-testid={`brand-package-${brand.id}`}
              >
                {/* Image with Category Tag */}
                <div className="relative h-44 overflow-hidden">
                  <img 
                    src={brand.image} 
                    alt={brand.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-white/95 text-[#2C332E] text-xs px-2 py-1 font-medium">
                      {brand.category.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {selectedBrand?.id === brand.id && (
                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#C77D63] flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Brand Info */}
                <CardHeader className="pb-3 pt-4">
                  <CardTitle className="text-xl text-[#2C332E] leading-tight">
                    {pricingTier === 'brand_kit' && brand.foundation_kit_name 
                      ? brand.foundation_kit_name 
                      : brand.name}
                  </CardTitle>
                  <CardDescription className="text-[#78716C] italic text-sm mt-1">
                    &ldquo;{pricingTier === 'brand_kit' && brand.foundation_kit_tagline 
                      ? brand.foundation_kit_tagline 
                      : brand.tagline}&rdquo;
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-1 space-y-3 pt-0">
                  {/* Short Description */}
                  <p className="text-[#78716C] text-sm leading-relaxed line-clamp-2">
                    {pricingTier === 'brand_kit'
                      ? `A thoughtfully designed brand foundation for the ${brand.category?.replace(/_/g, ' ')} space.`
                      : brand.description || `A complete brand package with a pre-secured domain.`
                    }
                  </p>
                  
                  {/* Domain Info for $297 - Softened */}
                  {pricingTier === 'exclusive' && brand.included_domain && (
                    <div className="flex items-center gap-2 text-sm text-[#78716C]">
                      <Globe className="w-4 h-4 text-[#C77D63]" />
                      <span>Includes secured domain: <span className="font-medium text-[#C77D63]">{brand.included_domain}</span></span>
                    </div>
                  )}

                  {/* Brand System Includes */}
                  <div>
                    <p className="font-medium text-[#2C332E] text-xs uppercase tracking-wide mb-2">
                      {pricingTier === 'exclusive' ? 'Brand System Includes' : 'Includes'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(pricingTier === 'brand_kit' 
                        ? ['Brand Direction', 'Logo Guidance', 'Color Palette', 'Voice & Values', 'Packaging Guide']
                        : (brand.includes?.slice(0, 5) || ['Brand Identity', 'Logo Concepts', 'Color Palette', 'Business Guide'])
                      ).map((item, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center gap-1 text-xs text-[#78716C] bg-[#F5F0EB] px-2 py-1 rounded-full"
                        >
                          <Check className="w-3 h-3 text-[#8DA399]" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>

                {/* Footer with CTA - No prices on cards for ownership tier */}
                <CardFooter className="pt-4 pb-5 border-t border-[#E6E1DB]/50 mt-auto">
                  <div className="w-full flex items-center justify-between">
                    {pricingTier === 'brand_kit' ? (
                      <span className="text-2xl font-bold text-[#2C332E]">$197</span>
                    ) : (
                      <div></div>
                    )}
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBrand(brand);
                        setTimeout(() => {
                          // For $297 exclusive tier, scroll to checkout section
                          // For $197 brand_kit tier, scroll to experience level section
                          const targetSection = pricingTier === 'exclusive' 
                            ? document.querySelector('[data-testid="checkout-section"]')
                            : document.querySelector('[data-testid="experience-section"]');
                          if (targetSection) {
                            targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      className={`rounded-full px-5 ${
                        selectedBrand?.id === brand.id
                          ? 'bg-[#C77D63] text-white'
                          : 'bg-[#8DA399] text-white hover:bg-[#7A8F86]'
                      }`}
                      data-testid="select-brand-btn"
                    >
                      {selectedBrand?.id === brand.id 
                        ? 'Selected' 
                        : (pricingTier === 'exclusive' ? 'View Ownership Details' : 'Select')
                      }
                      {selectedBrand?.id !== brand.id && <ArrowRight className="w-4 h-4 ml-1" />}
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {/* Section 3: Important Distinction - Global Footer for $197 */}
          {pricingTier === 'brand_kit' && (
            <div className="mb-12 p-6 bg-[#F5F0EB] rounded-xl border border-[#E6E1DB] text-center">
              <p className="text-[#78716C] mb-2">
                These brand foundations are non-exclusive and intended to provide direction, not ownership.
              </p>
              <p className="text-[#78716C] text-sm">
                If you are looking for a fully defined, one-time-sale brand with a secured domain, explore our <button onClick={() => setPricingTier('exclusive')} className="text-[#C77D63] hover:underline font-medium">Ready-Made Brand Ownership packages</button>.
              </p>
            </div>
          )}

          {/* FAQ Section - $197 Brand Foundation */}
          {pricingTier === 'brand_kit' && (
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-5 h-5 text-[#8DA399]" />
                <h3 className="text-xl font-semibold text-[#2C332E]">FAQ — $197 Brand Foundation</h3>
              </div>
              
              <div className="space-y-4">
                {/* FAQ Item 1 */}
                <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                  <h4 className="font-medium text-[#2C332E] mb-2">Is a domain included with the $197 brand foundation?</h4>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    No. The $197 option is focused on brand direction — not ownership or launch.<br />
                    This gives you space to explore your brand without committing to a name or domain too early.
                  </p>
                </div>

                {/* FAQ Item 2 */}
                <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                  <h4 className="font-medium text-[#2C332E] mb-2">Can I add a domain later if I choose the $197 option?</h4>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    Yes. Once your brand direction feels settled, you&apos;ll have the option to upgrade and add a domain.<br />
                    This happens inside the experience, not during checkout, so your early decisions stay flexible.
                  </p>
                </div>

                {/* FAQ Item 3 */}
                <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                  <h4 className="font-medium text-[#2C332E] mb-2">How does the domain upgrade work?</h4>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    If you choose to upgrade later, you&apos;ll be guided through domain options at that stage.<br />
                    You can either connect your own domain or select from a curated option, depending on availability.<br />
                    All setup steps are clearly guided — nothing is automatic or rushed.
                  </p>
                </div>

                {/* FAQ Item 4 */}
                <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                  <h4 className="font-medium text-[#2C332E] mb-2">Do I need to decide on a domain now?</h4>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    No. Domain selection is intentionally deferred.<br />
                    Your brand foundation comes first. Ownership comes when you&apos;re ready.
                  </p>
                </div>

                {/* FAQ Item 5 */}
                <div className="bg-white rounded-xl p-5 border border-[#E6E1DB]">
                  <h4 className="font-medium text-[#2C332E] mb-2">Will my brand be published or live after purchase?</h4>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    No. Nothing is published or launched with the $197 foundation.<br />
                    You move forward only when you intentionally choose to.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Guidance message when no brand selected yet - $197 tier only */}
          {!selectedBrand && pricingTier === 'brand_kit' && (
            <div className="mb-8 p-4 bg-[#F5F0EB] rounded-xl border border-[#E6E1DB] text-center">
              <p className="text-[#78716C] text-sm">
                <span className="font-medium text-[#2C332E]">Select a foundation to continue.</span><br />
                Choose a category above to proceed to checkout.
              </p>
            </div>
          )}
          </>
        )}

        {/* Experience Level Selection - Optional for $197 Brand Foundation */}
        {selectedBrand && pricingTier === 'brand_kit' && (
          <div className="mb-12 animate-fade-in-up" data-testid="experience-section">
            <h2 className="text-2xl font-semibold text-[#2C332E] mb-2 flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#8DA399]" />
              Select Your Experience Level
              <span className="text-sm font-normal text-[#78716C] ml-2">(Optional)</span>
            </h2>
            <p className="text-[#78716C] mb-6">
              Choose the path that matches your entrepreneurial journey. 
              <span className="text-[#8DA399]"> Default: Beginner resources included.</span>
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {experienceLevels.map((level) => {
                const isSelected = experienceLevel === level.id;
                const isDefault = !experienceLevel && level.id === 'beginner';
                const IconComponent = level.id === 'beginner' ? Rocket : TrendingUp;
                
                return (
                  <Card 
                    key={level.id}
                    className={`cursor-pointer transition-all duration-300 rounded-2xl overflow-hidden ${
                      isSelected
                        ? 'border-2 border-[#8DA399] shadow-hover bg-gradient-to-br from-[#8DA399]/5 to-[#C77D63]/5'
                        : isDefault
                        ? 'border-2 border-[#8DA399]/50 bg-[#8DA399]/5'
                        : 'border-2 border-[#E6E1DB] hover:border-[#8DA399]/50 hover:shadow-soft'
                    }`}
                    onClick={() => setExperienceLevel(level.id)}
                    data-testid={`experience-${level.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                          isSelected || isDefault
                            ? 'bg-[#8DA399] text-white' 
                            : 'bg-[#F5F0EB] text-[#8DA399]'
                        }`}>
                          <IconComponent className="w-7 h-7" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-lg text-[#2C332E]">
                              {level.title}
                              {isDefault && !isSelected && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-[#8DA399]/20 text-[#8DA399] rounded-full">Default</span>
                              )}
                            </h3>
                            {(isSelected || isDefault) && (
                              <div className="w-6 h-6 rounded-full bg-[#8DA399] flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-[#C77D63] font-medium mb-2">{level.subtitle}</p>
                          <p className="text-sm text-[#78716C] mb-4">{level.description}</p>
                          
                          <div className="space-y-2">
                            {level.features.map((feature, index) => (
                              <div key={index} className="flex items-center gap-2 text-sm">
                                <Check className={`w-4 h-4 flex-shrink-0 ${isSelected || isDefault ? 'text-[#8DA399]' : 'text-[#C77D63]'}`} />
                                <span className="text-[#78716C]">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Your Brand Is Ready - Confirmation card for $297 Exclusive tier */}
        {selectedBrand && pricingTier === 'exclusive' && (
          <div className="mb-12 animate-fade-in-up" data-testid="checkout-section">
            <h2 className="text-2xl font-semibold text-[#2C332E] mb-2 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#C77D63]" />
              Your Brand Is Ready
            </h2>
            <p className="text-[#78716C] mb-6">This brand is fully defined and ready to move forward. No setup choices are needed.</p>
            
            <Card className="border-2 border-[#C77D63] bg-gradient-to-br from-[#C77D63]/5 to-[#8DA399]/5 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#C77D63] flex items-center justify-center flex-shrink-0">
                    <Package className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-lg text-[#2C332E]">Your Brand Package</h3>
                      <div className="w-6 h-6 rounded-full bg-[#C77D63] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <p className="text-sm text-[#C77D63] font-medium">Ready-Made Brand (domain included)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Complete Your Purchase Section - Show when brand is selected */}
        {selectedBrand && (
          <div className="mb-12 animate-fade-in-up">
            {/* Selection Confirmed Message */}
            <div className="mb-6 p-4 bg-[#8DA399]/10 rounded-xl border border-[#8DA399]/30 text-center">
              <p className="text-[#2C332E] text-sm font-medium flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-[#8DA399]" />
                Your selection is saved. You can now proceed to checkout.
              </p>
            </div>
            
            {/* Section Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-[#2C332E] mb-3">Complete Your Purchase</h2>
              <p className="text-[#78716C] max-w-xl mx-auto">
                You&apos;ve selected {pricingTier === 'brand_kit' 
                  ? `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Brand Foundation Kit`
                  : `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Ready-Made Brand`
                }. Review your order and add any optional upgrades below.
              </p>
            </div>

            {/* Your Package - Ready Made Brand */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">🌿</span>
                <h3 className="text-lg font-semibold text-[#2C332E]">YOUR PACKAGE</h3>
              </div>
              
              <Card className="bg-gradient-to-br from-[#8DA399]/5 to-[#C77D63]/5 rounded-2xl border-2 border-[#8DA399]">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-[#2C332E]">
                        {pricingTier === 'brand_kit'
                          ? `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Brand Foundation Kit`
                          : `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Ready-Made Brand`
                        }
                      </h4>
                      <p className="text-[#C77D63] font-bold text-lg">${pricingTier === 'exclusive' ? '297' : '197'}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#8DA399] flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-[#78716C] mb-3">
                    {pricingTier === 'brand_kit'
                      ? `A complete brand foundation for ${selectedBrand.category?.replace(/_/g, ' ')} products — visuals, voice, packaging direction, and a starter formula to begin.`
                      : 'This is a complete, ready-made brand. Your brand name and domain are already secured and included. No additional setup or add-ons are required at checkout.'
                    }
                  </p>
                  {pricingTier === 'brand_kit' && (
                    <p className="text-xs text-[#8DA399] italic mb-3">
                      Brand name is a starter suggestion — you may rename it.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedBrand.includes?.slice(0, 5).map((item, index) => (
                      <span key={index} className="px-2 py-1 bg-white rounded-full text-xs text-[#78716C]">
                        ✓ {item}
                      </span>
                    ))}
                  </div>
                  
                  {/* Domain Info - Only show for $297 Exclusive tier */}
                  {pricingTier === 'exclusive' && selectedBrand.included_domain && (
                    <div className="mt-4 pt-4 border-t border-[#8DA399]/30">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#8DA399]" />
                        <span className="text-sm font-medium text-[#2C332E]">Domain included:</span>
                        <span className="text-sm text-[#C77D63] font-semibold">{selectedBrand.included_domain}</span>
                      </div>
                      <p className="text-xs text-[#78716C] mt-1 ml-6">Pre-secured and ready to claim after purchase.</p>
                    </div>
                  )}
                  
                  {/* Checkout mini-line for $197 */}
                  {pricingTier === 'brand_kit' && (
                    <p className="text-xs text-[#78716C] mt-4 pt-3 border-t border-[#E6E1DB]">
                      Domain chosen later. Add Domain Setup (+$29) below if you'd like guidance.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Optional Upgrades */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">➕</span>
                <h3 className="text-lg font-semibold text-[#2C332E]">OPTIONAL UPGRADES</h3>
              </div>
              <p className="text-sm text-[#78716C] mb-6">
                Only choose what feels helpful right now.
              </p>

              <div className="space-y-4">
                {/* Domain Setup - Only for Brand Kit tier */}
                {pricingTier === 'brand_kit' && (
                  <Card 
                    className={`rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      selectedAddOns.includes("domain_setup")
                        ? 'border-[#8DA399] bg-[#8DA399]/5'
                        : 'border-[#E6E1DB] hover:border-[#8DA399]/50'
                    }`}
                    onClick={() => toggleAddOn("domain_setup")}
                    data-testid="addon-domain_setup"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <Checkbox 
                          checked={selectedAddOns.includes("domain_setup")}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-[#2C332E]">Domain Setup (Optional)</h4>
                            <span className="ml-auto text-[#C77D63] font-bold">(+$29)</span>
                          </div>
                          <p className="text-sm text-[#78716C]">
                            A step-by-step guide to choosing and connecting your domain—when you're ready.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Formula Options Section */}
                <div className="flex items-center gap-2 mb-3 mt-6">
                  <span>🧪</span>
                  <h4 className="font-medium text-[#2C332E]">Formula Options</h4>
                </div>

                {/* Additional Formula Pack (3) */}
                <Card 
                  className={`rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    selectedAddOns.includes("formula_bundle_3")
                      ? 'border-[#8DA399] bg-[#8DA399]/5'
                      : 'border-[#E6E1DB] hover:border-[#8DA399]/50'
                  }`}
                  onClick={() => toggleAddOn("formula_bundle_3")}
                  data-testid="addon-formula_bundle_3"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <Checkbox 
                        checked={selectedAddOns.includes("formula_bundle_3")}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-[#2C332E]">Additional Formula Pack (3 Formulas)</h4>
                          <span className="text-[#C77D63] font-bold">+$47</span>
                        </div>
                        <p className="text-sm text-[#78716C] mb-2">
                          Expand your starting point with three additional beginner-friendly formulas aligned with your brand category.
                        </p>
                        <p className="text-xs text-[#8DA399]">
                          Designed to help you learn structure, texture, and consistency at a comfortable pace.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Advanced Formula Pack (5) */}
                <Card 
                  className={`rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    selectedAddOns.includes("formula_bundle_5")
                      ? 'border-[#8DA399] bg-[#8DA399]/5'
                      : 'border-[#E6E1DB] hover:border-[#8DA399]/50'
                  }`}
                  onClick={() => toggleAddOn("formula_bundle_5")}
                  data-testid="addon-formula_bundle_5"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <Checkbox 
                        checked={selectedAddOns.includes("formula_bundle_5")}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-[#2C332E]">Advanced Formula Pack (5 Formulas)</h4>
                          <span className="text-[#C77D63] font-bold">$77</span>
                        </div>
                        <p className="text-sm text-[#78716C] mb-2">
                          A deeper formula set for founders ready to explore more advanced formulations or expand their product line.
                        </p>
                        <p className="text-xs text-[#8DA399]">
                          Best suited for those with prior formulation experience or professional support.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Order Summary */}
            <Card id="checkout-section" className="bg-[#F5F0EB] rounded-2xl border-0 shadow-soft">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-2">
                  <span>🧾</span>
                  <h3 className="text-lg font-semibold text-[#2C332E]">ORDER SUMMARY</h3>
                </div>
                <p className="text-xs text-[#8DA399] mb-4">
                  You can adjust or remove optional upgrades before checkout.
                </p>
                
                {/* Summary Items */}
                <div className="bg-white rounded-xl p-4 mb-6">
                  <p className="font-medium text-[#2C332E] mb-3">Your Selection</p>
                  
                  {/* Brand Package - DISPLAY RULES based on tier */}
                  <div className="flex justify-between py-2 border-b border-[#E6E1DB]">
                    <div>
                      <span className="text-[#2C332E]">
                        🌿 {pricingTier === 'brand_kit'
                          ? `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Brand Foundation Kit`
                          : `${selectedBrand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Ready-Made Brand`
                        }
                      </span>
                    </div>
                    <span className="text-[#2C332E] font-medium">${pricingTier === 'exclusive' ? '297.00' : '197.00'}</span>
                  </div>

                  {/* Experience Level / Brand Package */}
                  <div className="flex justify-between py-2 border-b border-[#E6E1DB]">
                    <span className="text-[#78716C]">{pricingTier === 'exclusive' ? 'Your Brand Package' : 'Experience Level'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      pricingTier === 'exclusive'
                        ? 'bg-[#C77D63]/10 text-[#C77D63]'
                        : experienceLevel === 'beginner' 
                          ? 'bg-[#8DA399]/10 text-[#8DA399]' 
                          : 'bg-[#C77D63]/10 text-[#C77D63]'
                    }`}>
                      {pricingTier === 'exclusive' 
                        ? '✨ Ready-Made (domain included)' 
                        : experienceLevel === 'beginner' 
                          ? '🚀 Beginner' 
                          : '📈 Advanced'
                      }
                    </span>
                  </div>
                  
                  {/* Optional Add-ons */}
                  {pricingTier === 'brand_kit' && selectedAddOns.includes("domain_setup") && (
                    <div className="flex justify-between py-2 border-b border-[#E6E1DB]">
                      <span className="text-[#78716C]">🌐 Domain Setup</span>
                      <span className="text-[#2C332E]">+$29.00</span>
                    </div>
                  )}
                  {selectedAddOns.includes("formula_bundle_3") && (
                    <div className="flex justify-between py-2 border-b border-[#E6E1DB]">
                      <span className="text-[#78716C]">🧪 Additional Formula Pack (3)</span>
                      <span className="text-[#2C332E]">+$47.00</span>
                    </div>
                  )}
                  {selectedAddOns.includes("formula_bundle_5") && (
                    <div className="flex justify-between py-2 border-b border-[#E6E1DB]">
                      <span className="text-[#78716C]">🧪 Advanced Formula Pack (5)</span>
                      <span className="text-[#2C332E]">+$77.00</span>
                    </div>
                  )}
                  
                  {/* Total */}
                  <div className="flex justify-between pt-4 mt-2">
                    <span className="font-semibold text-[#2C332E]">Total</span>
                    <span className="text-2xl font-bold text-[#2C332E]">${calculateTotal().toFixed(2)}</span>
                  </div>
                  
                  {/* Domain included note - only for exclusive tier */}
                  {pricingTier === 'exclusive' && selectedBrand.included_domain && (
                    <p className="text-xs text-[#8DA399] text-center mt-3 pt-3 border-t border-[#E6E1DB]">
                      🌐 Includes domain: <span className="font-medium">{selectedBrand.included_domain}</span>
                    </p>
                  )}
                </div>

                {/* Add to Cart / Checkout Flow */}
                {!cartItem ? (
                  <div className="space-y-4">
                    <Button 
                      onClick={addToCart}
                      className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-6 text-lg hover-lift w-full flex items-center justify-center gap-2"
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </Button>
                    <p className="text-xs text-[#78716C] text-center">
                      You&apos;ll review everything before completing checkout.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Cart Item Display */}
                    <div className="bg-white rounded-xl p-4 border-2 border-[#8DA399]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="w-5 h-5 text-[#8DA399]" />
                          <span className="font-semibold text-[#2C332E]">Your Cart</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={removeFromCart}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                          data-testid="remove-from-cart-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-[#2C332E] font-medium">
                            {cartItem.pricingTier === 'brand_kit' 
                              ? `${cartItem.brand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Brand Foundation Kit`
                              : `${cartItem.brand.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Ready-Made Brand`
                            }
                          </p>
                          <p className="text-sm text-[#78716C]">
                            {cartItem.pricingTier === 'exclusive' 
                              ? '✨ Ready-Made (domain included)'
                              : cartItem.experienceLevel === 'beginner' 
                                ? '🚀 Beginner' 
                                : '📈 Advanced'
                            }
                            {cartItem.pricingTier === 'brand_kit' && cartItem.addOns.length > 0 && ` • ${cartItem.addOns.length} add-on${cartItem.addOns.length > 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <p className="font-bold text-[#2C332E]">${cartItem.total.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* PayPal Checkout */}
                    {user ? (
                      PAYPAL_CLIENT_ID && PAYPAL_CLIENT_ID !== "test" && PAYPAL_CLIENT_ID !== "YOUR_PAYPAL_CLIENT_ID" ? (
                        <div className="paypal-button-container" data-testid="paypal-buttons">
                          {/* Payment Method Info */}
                          <div className="text-center mb-4">
                            <p className="text-sm font-medium text-[#2C332E]">Payment Method</p>
                            <p className="text-xs text-[#78716C]">Secure checkout via PayPal.</p>
                            <p className="text-xs text-[#8DA399]">Complete your purchase using your PayPal balance, card, or linked bank account.</p>
                          </div>
                          <PayPalScriptProvider options={{ 
                            clientId: PAYPAL_CLIENT_ID,
                            currency: "USD",
                            intent: "capture"
                          }}>
                            <PayPalButtons
                              style={{
                                layout: "vertical",
                                color: "gold",
                                shape: "pill",
                                label: "paypal",
                                height: 50
                              }}
                              forceReRender={[cartItem?.total, cartItem?.addOns, cartItem?.pricingTier]}
                              createOrder={async () => {
                                try {
                                  const response = await axios.post(`${API}/store/checkout`, {
                                    package_id: cartItem.brand.id,
                                    add_ons: cartItem.addOns,
                                    pricing_tier: cartItem.pricingTier,
                                    experience_level: cartItem.experienceLevel
                                  });
                                  return response.data.order_id;
                                } catch (error) {
                                  console.error("PayPal order creation error:", error.response?.data || error);
                                  const errorMsg = error.response?.data?.detail || "Failed to create order. Please try again.";
                                  toast.error(errorMsg);
                                  throw error;
                                }
                              }}
                              onApprove={async (data) => {
                                try {
                                  const response = await axios.post(`${API}/store/capture-payment/${data.orderID}`);
                                  if (response.data.status === "COMPLETED") {
                                    toast.success("Payment successful! Thank you for your purchase.");
                                    clearReadyMadeCart(); // Clear ONLY Ready-Made cart (not Create Brand)
                                    setCartItem(null);
                                    navigate(`/store/success?order_id=${data.orderID}`);
                                  } else {
                                    toast.info("Payment is being processed...");
                                  }
                                } catch (error) {
                                  console.error("Payment capture error:", error.response?.data || error);
                                  toast.error("Failed to capture payment. Please contact support.");
                                }
                              }}
                              onError={(err) => {
                                console.error("PayPal error:", err);
                                toast.error("Payment failed. Please try again.");
                              }}
                              onCancel={() => {
                                toast.info("Payment cancelled.");
                              }}
                            />
                          </PayPalScriptProvider>
                          <p className="text-xs text-[#A8A29E] text-center mt-2">
                            Secure checkout powered by PayPal.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Button 
                            onClick={handleCheckout}
                            disabled={isCheckingOut}
                            className="bg-[#0070BA] hover:bg-[#003087] text-white rounded-full px-8 py-6 text-lg hover-lift w-full flex items-center justify-center gap-2"
                            data-testid="paypal-checkout-btn"
                          >
                            {isCheckingOut ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.83l-1.694 10.727h4.39a.563.563 0 0 0 .556-.479l.023-.118.442-2.8.028-.154a.563.563 0 0 1 .556-.479h.35c2.266 0 4.04-.92 4.559-3.58.216-1.112.104-2.04-.359-2.69a2.23 2.23 0 0 0-.559-.522z"/>
                                </svg>
                                Checkout with PayPal
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-amber-600 text-center bg-amber-50 rounded-lg p-2">
                            ⚠️ PayPal checkout is being configured. Please try again later or contact support.
                          </p>
                        </div>
                      )
                    ) : (
                      /* CHECKOUT REQUIRES SIGN-IN (Hard Stop) */
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-[#F5F0EB] rounded-xl border border-[#E6E1DB]">
                          <Lock className="w-8 h-8 text-[#C77D63] mx-auto mb-3" />
                          <h4 className="text-lg font-semibold text-[#2C332E] mb-2">Sign in to complete checkout</h4>
                          <p className="text-sm text-[#78716C] mb-1">
                            You can browse freely and add items to your cart.
                          </p>
                          <p className="text-sm text-[#78716C]">
                            To complete a purchase, you&apos;ll need to sign in — this helps protect brand ownership and ensures each transaction is intentional.
                          </p>
                        </div>
                        <Button 
                          onClick={handleLogin}
                          className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-6 text-lg hover-lift w-full flex items-center justify-center gap-2"
                          data-testid="checkout-btn"
                        >
                          <Lock className="w-5 h-5" />
                          Sign in to continue
                        </Button>
                        <button 
                          onClick={() => setCartItem(null)}
                          className="w-full text-sm text-[#78716C] hover:text-[#2C332E] transition-colors py-2"
                        >
                          Continue browsing
                        </button>
                        {/* Trust Footnote */}
                        <p className="text-xs text-[#78716C]/70 text-center italic">
                          Sign-in helps ensure each brand experience remains intentional and protected.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Reassurance Footer */}
                <div className="mt-6 pt-6 border-t border-[#E6E1DB]/50 text-center">
                  <p className="text-sm text-[#78716C] italic">
                    🧘‍♀️ You don&apos;t need to have everything figured out today.<br />
                    This is simply the next gentle step forward.
                  </p>
                </div>

                {/* Mini FAQ - Different content based on tier */}
                <div className="mt-6 pt-6 border-t border-[#E6E1DB]/50">
                  <h4 className="text-sm font-semibold text-[#2C332E] mb-4">A few quick answers</h4>
                  
                  {pricingTier === 'brand_kit' ? (
                    /* Brand Kit ($197) Mini FAQ */
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Is a domain included?</span>
                        <span className="text-[#78716C]">Chosen later, once your brand name feels final. Add Domain Setup below for guidance.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Do I get a formula?</span>
                        <span className="text-[#78716C]">Yes. One starter formula is included.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">What happens after purchase?</span>
                        <span className="text-[#78716C]">You'll connect your domain and go live when DNS is in place.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Do I need technical skills?</span>
                        <span className="text-[#78716C]">No. It's simple copy/paste setup. If you're stuck, we'll guide you.</span>
                      </div>
                    </div>
                  ) : (
                    /* Exclusive ($297) Mini FAQ */
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Is a domain included?</span>
                        <span className="text-[#78716C]">Yes. A pre-secured domain is included with this brand.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Is this brand exclusive?</span>
                        <span className="text-[#78716C]">Yes. It's sold once and removed after purchase.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">What happens after purchase?</span>
                        <span className="text-[#78716C]">Claim Domain → Connect to Emergent → Go live.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#8DA399] font-medium whitespace-nowrap">Do I need technical skills?</span>
                        <span className="text-[#78716C]">No. It's simple copy/paste setup. If you're stuck, we'll guide you.</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 text-center">
                    <Button
                      variant="link"
                      onClick={() => navigate('/faq')}
                      className="text-[#C77D63] hover:text-[#B06A52] text-sm p-0 h-auto"
                    >
                      View questions →
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section 4: Store CTA */}
        <div className="mt-16 mb-12 text-center bg-gradient-to-br from-[#8DA399]/10 to-[#C77D63]/10 rounded-2xl p-10">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#2C332E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Build With Confidence?
          </h2>
          <p className="text-[#78716C] max-w-xl mx-auto mb-8">
            Choose the level of support that matches where you are right now — from guided creation to fully owned, launch-ready brands.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/create')}
              className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3"
            >
              Start Creating Your Brand
            </Button>
            <Button 
              onClick={() => setPricingTier('exclusive')}
              variant="outline"
              className="border-[#8DA399] text-[#8DA399] hover:bg-[#8DA399]/10 rounded-full px-8 py-3"
            >
              Explore Ownership-Ready Brands
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-[#2C332E] text-center mb-8">Every Brand Includes</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Palette, title: "Color Palette", desc: "5 curated colors with usage guide" },
              { icon: FileText, title: "Brand Identity", desc: "Logo concepts & typography" },
              { icon: Sparkles, title: "Starter Formula", desc: "1 product formula included" },
              { icon: Download, title: "PDF Guides", desc: "Downloadable brand assets" }
            ].map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-white shadow-soft">
                <div className="w-12 h-12 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-[#8DA399]" />
                </div>
                <h3 className="font-semibold text-[#2C332E] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#78716C]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Context Warning Modal - shown when user tries to add to Ready-Made cart while having Create Brand items */}
      {showContextWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#2C332E]">Active Cart Detected</h3>
            </div>
            <p className="text-[#78716C] mb-6">
              You have items in your Create-a-Brand cart. You can only have one active cart at a time.
              Please complete or clear that cart before adding Ready-Made Brand items.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowContextWarning(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowContextWarning(false);
                  navigate('/dashboard');
                }}
                className="flex-1 bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Store;
